import { useCallback } from 'react'
import { CompositeImage } from '../../../interfaces/notification-field-suggestions'
import { getFileBlobFromUrl, getImageFromBlob } from '../../../_utils/images'
import { generateUID } from '../../../_utils/uid'
import { noop } from '../../../_utils/utils'
import { PinturaInternals } from '@pushly/aqe/lib/components/file-uploader/utils'
import { useService } from '../../../hooks/use-service'
import { FileUploadService } from '@pushly/aqe/lib/services/file-upload.service'
import { DomainDto } from '../../../dtos/domain'

const { processImage, createDefaultImageReader, createDefaultImageWriter } = PinturaInternals

type MediaDomain = Pick<DomainDto, 'id' | 'mediaQuality'>

export const useCompositeImageLoader = ({ id: domainId, mediaQuality = 40 }: MediaDomain) => {
    const fileUploadSvc = useService(FileUploadService)

    return useCallback(
        async (imageURL: string): Promise<CompositeImage | null> => {
            if (!imageURL) return null

            try {
                const blob = await getFileBlobFromUrl(imageURL)
                if (!blob) {
                    return null
                }

                const img = await getImageFromBlob(blob)

                const imageCropAspectRatio = 2
                let width = img.width
                let height = img.height

                if (width / height !== imageCropAspectRatio) {
                    if (width > height) {
                        width =
                            img.width / img.height > imageCropAspectRatio
                                ? img.height * imageCropAspectRatio
                                : img.width
                    }

                    height = width / imageCropAspectRatio
                }

                const processed = await processImage(blob as any, {
                    imageReader: createDefaultImageReader(),
                    imageWriter: createDefaultImageWriter({
                        targetSize: {
                            width: 400,
                            height: 200,
                            fit: 'cover',
                            upscale: false,
                        },
                        quality: mediaQuality,
                    }),
                    imageCropAspectRatio: 2,
                    imageCrop: {
                        x: img.width / 2 - width / 2,
                        y: img.height / 2 - height / 2,
                        width,
                        height,
                    },
                    imageTargetSize: {
                        width: 400,
                        height: 200,
                    },
                })

                const uploaded = await fileUploadSvc.processFormDataUpload(
                    'image',
                    'domain',
                    domainId,
                    'file',
                    processed.dest,
                    'public-read',
                    {
                        tmpId: generateUID(),
                        originalMediaUrl: imageURL,
                        quality: mediaQuality,
                        progressive: true,
                    },
                    noop,
                )

                return {
                    ...processed,
                    url: uploaded.url,
                    filename: uploaded.filename,
                    originalMediaUrl: imageURL,
                }
            } catch (err) {
                console.error('Suggested Image Loading Error', err)
            }

            return null
        },
        [domainId, mediaQuality],
    )
}
